import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users, Search, MapPin, Briefcase, MessageCircle, ChevronRight, ChevronLeft, Star, UserPlus,
  Sparkles, Check, X, Eye, Target, Globe, Network, MessagesSquare
} from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { id: 'discover', label: 'Discover', badge: null },
  { id: 'connections', label: 'Connections', badge: 'count' },
  { id: 'opportunities', label: 'Opportunities', badge: 'NEW' },
  { id: 'requests', label: 'Requests', badge: 'pending' },
  { id: 'groups', label: 'Groups', badge: null },
  { id: 'community', label: 'Community', badge: null },
];

const ProfessionalCard = ({ professional, onConnect, connecting, showMutual = true }) => (
  <Card className="overflow-hidden hover:shadow-lg transition-all group flex-shrink-0 w-full" data-testid={`pro-card-${professional.id}`}>
    <CardContent className="p-5">
      <div className="flex flex-col items-center text-center">
        <Link to={`/card/${professional.id}`}>
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center overflow-hidden mb-3">
            {professional.profile_photo || professional.photo ? (
              <img src={resolveImageUrl(professional.profile_photo || professional.photo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-2xl font-bold">{(professional.full_name || professional.name || 'U')[0]}</span>
            )}
          </div>
        </Link>
        <Link to={`/card/${professional.id}`} className="hover:text-primary">
          <h4 className="font-semibold text-sm">{professional.full_name || professional.name}</h4>
        </Link>
        <p className="text-xs text-primary font-medium mt-0.5">{professional.profession}</p>
        {professional.company && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-full">{professional.company}</p>
        )}
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
          <MapPin className="w-3 h-3" />
          <span>{professional.city}{professional.state ? `, ${professional.state}` : ''}</span>
        </div>
        {showMutual && professional.mutual_connections > 0 && (
          <p className="text-xs text-primary mt-1.5">{professional.mutual_connections} mutual connections</p>
        )}
        {professional.match_score > 0 && (
          <div className="flex items-center gap-1.5 mt-2 w-full">
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full" style={{ width: `${professional.match_score}%` }} />
            </div>
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{professional.match_score}% match</span>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-4 pt-3 border-t border-border/50">
        <Button size="sm" className="flex-1 h-9" onClick={() => onConnect(professional.id)} disabled={connecting[professional.id]} data-testid={`connect-btn-${professional.id}`}>
          {connecting[professional.id] ? '...' : (<><UserPlus className="w-3.5 h-3.5 mr-1" />Connect</>)}
        </Button>
        <Link to={`/card/${professional.id}`}>
          <Button size="sm" variant="outline" className="h-9 px-3"><Eye className="w-3.5 h-3.5" /></Button>
        </Link>
      </div>
    </CardContent>
  </Card>
);

// Horizontal scrollable carousel with left/right arrows
const CardCarousel = ({ children, testId }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll, children]);

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-testid^="pro-card-"]')?.offsetWidth || el.clientWidth / 4;
    el.scrollBy({ left: dir * (cardWidth + 16), behavior: 'smooth' });
  };

  return (
    <div className="relative group/carousel" data-testid={testId}>
      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-9 h-9 rounded-full bg-background border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-colors opacity-0 group-hover/carousel:opacity-100"
          data-testid="carousel-left"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-9 h-9 rounded-full bg-background border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-colors opacity-0 group-hover/carousel:opacity-100"
          data-testid="carousel-right"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scroll-smooth px-1 pb-2 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
    </div>
  );
};

const RequestCard = ({ request, onAccept, onDecline, loading }) => {
  const sender = request.sender || request;
  return (
    <Card className="overflow-hidden" data-testid={`request-${request.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Link to={`/card/${sender.id}`}>
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center overflow-hidden">
              {sender.photo ? (
                <img src={resolveImageUrl(sender.photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-bold">{(sender.name || 'U')[0]}</span>
              )}
            </div>
          </Link>
          <div className="flex-1 min-w-0">
            <Link to={`/card/${sender.id}`} className="hover:text-primary">
              <h4 className="font-semibold text-sm">{sender.name}</h4>
            </Link>
            <p className="text-xs text-muted-foreground">{sender.profession}</p>
            {sender.company && <p className="text-xs text-muted-foreground">{sender.company}</p>}
            {request.message && <p className="text-xs text-muted-foreground mt-2 italic">"{request.message}"</p>}
          </div>
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-border/50">
          <Button size="sm" className="flex-1 h-8 bg-green-600 hover:bg-green-700" onClick={() => onAccept(request.id || request.connection_id)} disabled={loading} data-testid={`accept-${request.id}`}>
            <Check className="w-3.5 h-3.5 mr-1" />Accept
          </Button>
          <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => onDecline(request.id || request.connection_id)} disabled={loading}>Decline</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const GroupCardMini = ({ group, onJoin, isMember }) => (
  <Card className="overflow-hidden hover:shadow-lg transition-all" data-testid={`group-${group.id}`}>
    <div className="h-20 bg-gradient-to-br from-primary/30 to-purple-500/30 relative">
      {group.cover_image && <img src={resolveImageUrl(group.cover_image)} alt="" className="w-full h-full object-cover" />}
    </div>
    <CardContent className="p-3">
      <h4 className="font-semibold text-sm truncate">{group.name}</h4>
      <p className="text-xs text-muted-foreground">{group.members?.length || 0} members</p>
      <Button size="sm" variant={isMember ? 'outline' : 'default'} className="w-full mt-2 h-7 text-xs" onClick={() => onJoin(group.id)} data-testid={`join-group-${group.id}`}>
        {isMember ? 'Member' : 'Join'}
      </Button>
    </CardContent>
  </Card>
);

const PARTNER_TAGS = [
  'Mortgage Brokers', 'Real Estate Agents', 'Insurance Agents', 'Financial Advisors',
  'Attorneys', 'Accountants', 'Home Inspectors', 'Title Companies'
];

export default function NetworkPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'discover');
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState({});
  const [recommended, setRecommended] = useState([]);
  const [connections, setConnections] = useState({ accepted: [], pending_received: [] });
  const [groups, setGroups] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [recData, connData, groupsData] = await Promise.all([
        api.getRecommendedProfessionals(20).catch(() => ({ recommended: [] })),
        api.getConnections().catch(() => ({ accepted: [], pending_received: [] })),
        api.getGroups().catch(() => ({ groups: [] })),
      ]);
      setRecommended(recData.recommended || []);
      setConnections(connData);
      setGroups(groupsData.groups || []);
    } catch (error) { console.error('Failed to fetch network data:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tab) => { setActiveTab(tab); setSearchParams({ tab }); };

  const handleConnect = async (userId) => {
    setConnecting(prev => ({ ...prev, [userId]: true }));
    try {
      await api.sendConnectionRequest(userId, "I'd like to connect with you on ConnectMail!");
      toast.success('Connection request sent!');
      setRecommended(prev => prev.filter(p => p.id !== userId));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send request'); }
    finally { setConnecting(prev => ({ ...prev, [userId]: false })); }
  };

  const handleAccept = async (requestId) => {
    try { await api.acceptConnectionRequest(requestId); toast.success('Connection accepted!'); fetchData(); }
    catch { toast.error('Failed to accept'); }
  };

  const handleDecline = async (requestId) => {
    try { await api.declineConnectionRequest(requestId); toast.success('Request declined'); fetchData(); }
    catch { toast.error('Failed to decline'); }
  };

  const handleJoinGroup = async (groupId) => {
    try { await api.joinGroup(groupId); fetchData(); toast.success('Group membership updated!'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed to join group'); }
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const filteredRecommended = selectedTags.length > 0
    ? recommended.filter(p => selectedTags.some(tag =>
        p.profession?.toLowerCase().includes(tag.toLowerCase().replace('s', '')) ||
        p.business_category?.toLowerCase().includes(tag.toLowerCase())
      ))
    : recommended;

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded-lg" />
          <div className="h-40 bg-muted rounded-xl" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-48 bg-muted rounded-xl" />)}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="network-page">
        {/* Tab Navigation */}
        <div className="border-b border-border">
          <div className="flex gap-1 overflow-x-auto pb-px">
            {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              let badgeContent = null;
              if (tab.badge === 'count') badgeContent = connections.accepted?.length || 0;
              else if (tab.badge === 'pending') badgeContent = connections.pending_received?.length || null;
              else if (tab.badge === 'NEW') badgeContent = 'NEW';
              return (
                <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                  className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}
                  data-testid={`tab-${tab.id}`}>
                  {tab.label}
                  {badgeContent !== null && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${tab.badge === 'NEW' ? 'bg-green-100 text-green-700' : badgeContent > 0 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {badgeContent}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Discover Tab */}
        {activeTab === 'discover' && (
          <div className="space-y-8">
            <Card className="bg-gradient-to-r from-primary/5 via-purple-500/5 to-indigo-500/5 border-primary/20">
              <CardContent className="p-6 text-center">
                <div className="flex justify-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 blur-sm" />
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 blur-sm -ml-3" />
                  <div className="w-10 h-10 rounded-full bg-indigo-500/20 blur-sm -ml-3" />
                </div>
                <h2 className="text-xl font-bold mb-2">
                  ConnectMail AI found <span className="text-primary">{recommended.length}</span> professionals your network knows
                </h2>
                <p className="text-muted-foreground text-sm mb-4">
                  Based on your target market: {user?.target_businesses?.join(', ') || 'professionals in your area'}
                </p>
                <Link to="/explore">
                  <Button className="bg-primary hover:bg-primary/90" data-testid="see-all-matches-btn">
                    <Sparkles className="w-4 h-4 mr-2" />See All Matches
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold">Suggested Connections</h3>
                  <p className="text-sm text-muted-foreground">Connect with these business owners to strengthen your network</p>
                </div>
                <Link to="/directory"><Button variant="ghost" size="sm">See All <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
              </div>
              <CardCarousel testId="suggested-connections-carousel">
                {filteredRecommended.map(pro => (
                  <div key={pro.id} className="w-[calc(25%-12px)] min-w-[220px] flex-shrink-0">
                    <ProfessionalCard professional={pro} onConnect={handleConnect} connecting={connecting} />
                  </div>
                ))}
              </CardCarousel>
              {filteredRecommended.length === 0 && (
                <Card className="py-8"><CardContent className="text-center"><Users className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" /><p className="text-muted-foreground">No suggestions match your filters</p></CardContent></Card>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold">Referral Partners</h3>
                  <p className="text-sm text-muted-foreground">Partners you can share referrals with</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {PARTNER_TAGS.map(tag => (
                  <button key={tag} onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${selectedTags.includes(tag) ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80 text-muted-foreground'}`}
                    data-testid={`tag-${tag.toLowerCase().replace(/ /g, '-')}`}>
                    {tag}
                  </button>
                ))}
              </div>
              <CardCarousel testId="referral-partners-carousel">
                {filteredRecommended.map(pro => (
                  <div key={pro.id} className="w-[calc(25%-12px)] min-w-[220px] flex-shrink-0">
                    <ProfessionalCard professional={pro} onConnect={handleConnect} connecting={connecting} showMutual={false} />
                  </div>
                ))}
              </CardCarousel>
            </section>
          </div>
        )}

        {/* Connections Tab */}
        {activeTab === 'connections' && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold">Your Connections ({connections.accepted?.length || 0})</h3>
            {connections.accepted?.length > 0 ? (
              <CardCarousel testId="connections-carousel">
                {connections.accepted.map(conn => (
                  <div key={conn.user_id || conn.id} className="w-[calc(25%-12px)] min-w-[220px] flex-shrink-0">
                    <ProfessionalCard
                      professional={{ id: conn.user_id || conn.id, full_name: conn.name, profession: conn.profession, company: conn.company, city: conn.city, photo: conn.photo }}
                      onConnect={() => {}} connecting={{}} showMutual={false} />
                  </div>
                ))}
              </CardCarousel>
            ) : (
              <Card className="py-12"><CardContent className="text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                <h4 className="font-semibold mb-2">No connections yet</h4>
                <p className="text-sm text-muted-foreground mb-4">Start building your network by connecting with professionals</p>
                <Button onClick={() => handleTabChange('discover')} data-testid="discover-btn"><Search className="w-4 h-4 mr-2" />Discover Professionals</Button>
              </CardContent></Card>
            )}
          </div>
        )}

        {/* Opportunities Tab */}
        {activeTab === 'opportunities' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-6 text-center">
                <Target className="w-12 h-12 mx-auto text-green-600 mb-3" />
                <h3 className="text-lg font-bold text-green-800 mb-2">Referral Opportunities</h3>
                <p className="text-sm text-green-600 mb-4">Professionals in your network who could benefit from your services</p>
                <Badge className="bg-green-100 text-green-700">Coming Soon</Badge>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-6">
            <h3 className="text-lg font-bold">Connection Requests ({connections.pending_received?.length || 0})</h3>
            {connections.pending_received?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {connections.pending_received.map(req => (
                  <RequestCard key={req.connection_id || req.id} request={req} onAccept={handleAccept} onDecline={handleDecline} loading={false} />
                ))}
              </div>
            ) : (
              <Card className="py-12"><CardContent className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                <h4 className="font-semibold mb-2">No pending requests</h4>
                <p className="text-sm text-muted-foreground">You're all caught up!</p>
              </CardContent></Card>
            )}
          </div>
        )}

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Groups</h3>
              <Link to="/groups"><Button data-testid="see-all-groups-btn">See All Groups <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/groups?filter=recommended">
                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center"><Star className="w-6 h-6 text-white" /></div>
                    <h4 className="font-semibold group-hover:text-primary">Recommended</h4>
                    <p className="text-xs text-muted-foreground">Groups for you</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/groups?filter=location">
                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center"><MapPin className="w-6 h-6 text-white" /></div>
                    <h4 className="font-semibold group-hover:text-primary">By Location</h4>
                    <p className="text-xs text-muted-foreground">Groups in your area</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/groups?filter=industry">
                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center"><Briefcase className="w-6 h-6 text-white" /></div>
                    <h4 className="font-semibold group-hover:text-primary">By Industry</h4>
                    <p className="text-xs text-muted-foreground">Industry groups</p>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/groups?filter=new">
                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center"><UserPlus className="w-6 h-6 text-white" /></div>
                    <h4 className="font-semibold group-hover:text-primary">New Members</h4>
                    <p className="text-xs text-muted-foreground">Recently joined</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Your Groups</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {groups.slice(0, 6).map(group => (
                  <GroupCardMini key={group.id} group={group} onJoin={handleJoinGroup} isMember={group.members?.includes(user?.id)} />
                ))}
              </div>
            </div>
          </div>
        )}
        {activeTab === 'community' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">Community</h3>
              <Link to="/community"><Button data-testid="go-to-community-btn">Open Community Hub <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/community">
                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <MessagesSquare className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold group-hover:text-primary">Community Discussions</h4>
                      <p className="text-sm text-muted-foreground">Join conversations, share insights, and connect with the community</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/community">
                <Card className="hover:shadow-lg transition-all cursor-pointer group">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold group-hover:text-primary">Private Messages</h4>
                      <p className="text-sm text-muted-foreground">Direct conversations with your connections</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
